/**
 * verifyOTP — RPC contract for the email-OTP verify callable.
 *
 * Cross-process contract between `web/src/auth/session.api.ts`
 * (caller) and `firebase/src/auth/verifyOTP.api.ts` (the handler, an action on the
 * `identityOtp` router — `identityOtp.action.ts`).
 * Both ends parse with `.strict()` — an extra key fails the whole request.
 *
 * Fields justified:
 *   orgId — the organization whose sign-in this is, matching the sendOTP
 *            call that issued the code. Its own code is checked first, then
 *            a platform-scope one, which sendOTP issues when this
 *            organization admits the address only platform-wide; the scope
 *            the code was found in decides what the sign-in opens. Absent
 *            means platform scope: the code was sealed under the platform
 *            key. Nullish for the
 *            same reason as sendOTP's.
 *   email — with orgId, identifies which otps/{sha256(scope:email)} document
 *            to validate against
 *   code  — the 6-digit numeric code the member typed; regex-gated so
 *            non-numeric or wrong-length strings are rejected at the parse
 *            seam on both ends, before the handler even runs
 *
 * Output:
 *   token       — Firebase custom token returned in the TLS response body only;
 *                 the FE calls signInWithCustomToken immediately and never
 *                 stores it
 *   isNewUser   — true when no `users/{uid}` Firestore doc existed for this
 *                 account before this verify call; the FE uses it to decide
 *                 whether to show the JIT consent screen. Derived from
 *                 Firestore doc existence, not Auth-account existence — a
 *                 sign-in abandoned before consent leaves an Auth record with
 *                 no `users/{uid}` doc.
 *   displayName — the one resolved name source for the consent screen's
 *                 `users/{uid}` write: on invite redemption, the invite's own
 *                 required `displayName`; on scripted-provisioning sign-in,
 *                 the Auth account's `displayName` (set at provisioning time
 *                 by provision-member.ts), or null if somehow absent. Only
 *                 ever populated on an ALREADY-successful verification (the
 *                 OTP proved inbox possession first), so returning it widens
 *                 nothing about whether an email exists — anti-enumeration
 *                 holds. The FE falls back to an email-local-part default
 *                 only when this is null, never treating that fallback as an
 *                 identity fact.
 *   workspaceId — the organization this sign-in opened: under organization
 *                 scope, that orgId; under platform scope (whichever page
 *                 asked), the person's saved
 *                 selection while its grant holds, else the organization they
 *                 founded, else their oldest grant. Absent only for a
 *                 platform-scope address whose domain `platform/founding`
 *                 lists and which holds no grant and no approval: signed in
 *                 with no organization, to found one. A verify that can open
 *                 none and admits no founding throws instead. The client's
 *                 consent write sets `users/{uid}.workspaceId` from it, because
 *                 the server never creates `users/{uid}`; named for the field
 *                 it lands in.
 *
 * Excluded:
 *   uid        — internal server concern; never crosses the wire
 *   codeCipher — server-only; the code envelope-encrypted at rest in
 *                otps/{sha256(scope:email)}, never returned
 *   attempts   — server-only rate-limit counter
 *
 * Errors (thrown HttpsError, not part of the Output schema):
 *   invalid-argument / invalid-or-expired — every failure path that runs
 *     BEFORE the code is confirmed correct (missing/expired/wrong code, too
 *     many attempts, no member/invite at all) — no enumeration.
 *   failed-precondition / invite-expired — thrown only AFTER the code
 *     already matched: the invite backing this send has since lapsed
 *     (expired or revoked). Distinct from the generic error because inbox
 *     possession is already proven at that point; the FE reads this apart
 *     from a wrong code so it can say "ask for a new invite" instead of
 *     "check your code".
 */
import { z } from "zod";
import { Id } from "./workspace.entity.ts";

export const VerifyOTPInput = z
	.object({
		orgId: Id.nullish(),
		email: z
			.string()
			.email()
			.describe("the email address the code was sent to"),
		code: z
			.string()
			.regex(/^\d{6}$/)
			.describe("the 6-digit numeric one-time code"),
	})
	.strict();
export type VerifyOTPInput = z.infer<typeof VerifyOTPInput>;

export const VerifyOTPOutput = z
	.object({
		token: z
			.string()
			.min(1)
			.describe("Firebase custom token for the authenticated session"),
		isNewUser: z
			.boolean()
			.describe(
				"true when no `users/{uid}` Firestore doc existed for this account prior to this verification",
			),
		displayName: z
			.string()
			.nullish()
			.describe(
				"the resolved display name for the consent screen — the invite's displayName on invite redemption, or the Auth account's displayName on scripted provisioning; null when neither source has one",
			),
		workspaceId: Id.optional().describe(
			"the organization this sign-in opened, which the consent write saves as the person's active organization; absent for a listed-domain founder who has none yet",
		),
	})
	.strict();
export type VerifyOTPOutput = z.infer<typeof VerifyOTPOutput>;
